import { useState, useEffect } from 'react';
import { Clock, ChefHat, Package, CheckCircle, XCircle, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import PickupVerificationModal from './PickupVerificationModal';
import {
  formatEasternPickupDate,
  formatOrderNumberLabel,
  vendorApprovalDeadline,
} from '../lib/orderUtils';

const ACCENT = '#B00BF0';

const STATUS_CONFIG = {
  confirmed: { label: 'New', color: 'bg-blue-100 text-blue-800', nextStatus: 'ready', nextLabel: 'Mark Ready' },
  pending_vendor_approval: { label: 'Awaiting Vendor', color: 'bg-yellow-100 text-yellow-800', nextStatus: 'confirmed', nextLabel: 'Approve Order' },
  preparing: { label: 'Preparing', color: 'bg-orange-100 text-orange-800', nextStatus: 'ready', nextLabel: 'Mark Ready' },
  ready: { label: 'Ready', color: 'bg-green-100 text-green-800', nextStatus: 'picked_up', nextLabel: 'Mark Picked Up' },
  picked_up: { label: 'Picked Up', color: 'bg-gray-100 text-gray-800', nextStatus: null, nextLabel: null },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', nextStatus: null, nextLabel: null }
};

function paymentBadge(order) {
  if (order.status === 'pending_vendor_approval') {
    return { label: 'Payment held', className: 'bg-amber-100 text-amber-900' };
  }
  if (['confirmed', 'preparing', 'ready', 'picked_up'].includes(order.status)) {
    return { label: 'Paid', className: 'bg-green-100 text-green-800' };
  }
  return null;
}

export default function VendorOrderCard({ order, onUpdateStatus, onCancel, approveBlocked = false }) {
  const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.confirmed;
  const [countdown, setCountdown] = useState('');
  const [acceptCountdown, setAcceptCountdown] = useState('');
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const payment = paymentBadge(order);
  const isSkipTheLine = order.pickup_mode === 'skip_the_line' || Number(order.skip_the_line_fee || 0) > 0;
  const opensOnLabel = approveBlocked ? formatEasternPickupDate(order) : '';
  
  useEffect(() => {
    const updateCountdown = () => {
      const pickup = new Date(order.scheduled_pickup_time);
      const now = new Date();
      const diff = pickup - now;
      
      if (diff < 0) {
        setCountdown('Overdue');
        return;
      }
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      
      if (minutes < 60) {
        setCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      } else {
        setCountdown(`${Math.floor(minutes / 60)}h ${minutes % 60}m`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [order.scheduled_pickup_time]);

  useEffect(() => {
    if (order.status !== 'pending_vendor_approval' || approveBlocked) {
      setAcceptCountdown('');
      return;
    }
    const updateAccept = () => {
      const deadline = vendorApprovalDeadline(order, 15);
      if (!deadline) {
        setAcceptCountdown('—');
        return;
      }
      const diff = deadline - new Date();
      if (diff <= 0) {
        setAcceptCountdown('Expired');
        return;
      }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setAcceptCountdown(`${m}:${s.toString().padStart(2, '0')}`);
    };
    updateAccept();
    const interval = setInterval(updateAccept, 1000);
    return () => clearInterval(interval);
  }, [order, approveBlocked]);

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Toronto'
    });
  };

  const isOverdue = countdown === 'Overdue';
  const canCancel = ['pending_vendor_approval', 'confirmed', 'preparing', 'ready'].includes(order.status);
  const readyByIso = order.vendor_ready_by
    || (order.scheduled_pickup_time ? new Date(new Date(order.scheduled_pickup_time).getTime() - 5 * 60000).toISOString() : null);
  const readyByOverdue = !!readyByIso && new Date(readyByIso) < new Date();

  const handleStatusAction = () => {
    if (approveBlocked && order.status === 'pending_vendor_approval') return;
    if (order.status === 'ready' && config.nextStatus === 'picked_up') {
      setPickupModalOpen(true);
      return;
    }
    if (config.nextStatus) {
      onUpdateStatus(order.order_id, config.nextStatus);
    }
  };

  const handlePickupVerified = async (orderId, pickupCode) => {
    const ok = await onUpdateStatus(orderId, 'picked_up', pickupCode);
    if (ok) setPickupModalOpen(false);
  };

  return (
    <div 
      className={`bg-card rounded-xl border-2 shadow-sm overflow-hidden ${
        isOverdue && order.status !== 'cancelled' && order.status !== 'picked_up' 
          ? 'border-red-300' : ''
      }`}
      style={{ borderColor: isOverdue && canCancel ? undefined : `${ACCENT}33` }}
      data-testid={`vendor-order-${order.order_id}`}
    >
      <div className={`p-4 border-b ${isOverdue && canCancel ? 'bg-red-50' : 'bg-muted/30'}`}>
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-sm font-bold" style={{ color: ACCENT }}>
            {formatOrderNumberLabel(order)}
          </span>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {isSkipTheLine && (
              <Badge className="bg-primary text-primary-foreground gap-1" data-testid="skip-the-line-badge">
                <Zap className="w-3 h-3 fill-current" />
                Skip the line
              </Badge>
            )}
            {payment && (
              <Badge className={payment.className}>{payment.label}</Badge>
            )}
            <Badge className={config.color}>{config.label}</Badge>
          </div>
        </div>

        {readyByIso && (
          <p className={`text-2xl font-bold tracking-tight mb-2 ${readyByOverdue ? 'text-red-600' : ''}`} style={readyByOverdue ? undefined : { color: ACCENT }}>
            READY BY {formatTime(readyByIso).toUpperCase()}
          </p>
        )}

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>Pickup: {formatTime(order.scheduled_pickup_time)}</span>
          </div>
          <span className={`font-mono font-medium ${isOverdue ? 'text-red-600' : ''}`}>
            {countdown}
          </span>
        </div>

        {order.status === 'pending_vendor_approval' && !approveBlocked && (
          <div className="mt-2 text-xs">
            <span className="text-muted-foreground">Accept by:</span>{' '}
            <span className={`font-mono ${acceptCountdown === 'Expired' ? 'text-red-600' : 'text-primary'}`}>
              {acceptCountdown || '—'}
            </span>
          </div>
        )}
        {approveBlocked && order.status === 'pending_vendor_approval' && (
          <div className="mt-2 text-xs text-muted-foreground">
            Opens for approval on {opensOnLabel || 'pickup day'}
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="space-y-2 mb-4">
          {order.items.map((item, i) => (
            <div key={i} className="text-xl font-semibold">
              <span>{item.quantity}×</span> {item.name}
              {item.modifiers?.length > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-1">
                  ({item.modifiers.join(', ')})
                </span>
              )}
            </div>
          ))}
        </div>

        {order.special_instructions && (
          <div className="text-sm bg-yellow-50 text-yellow-800 p-2 rounded-lg mb-4">
            <span className="font-medium">Note:</span> {order.special_instructions}
          </div>
        )}

        {order.cancellation_reason && (
          <div className="text-sm bg-red-50 text-red-800 p-2 rounded-lg mb-4">
            <span className="font-medium">Cancelled:</span> {order.cancellation_reason}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t">
          <p className="text-base">{order.student_name}</p>
          
          <div className="flex items-center gap-2">
            {canCancel && onCancel && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onCancel(order.order_id)}
                className="text-destructive hover:text-destructive"
                data-testid={`cancel-order-${order.order_id}`}
              >
                <XCircle className="w-4 h-4" />
              </Button>
            )}
            
            {config.nextStatus && !(approveBlocked && order.status === 'pending_vendor_approval') && (
              <Button 
                size="sm"
                onClick={handleStatusAction}
                className={['confirmed', 'preparing'].includes(order.status) ? 'bg-green-600 hover:bg-green-700' : ''}
                style={order.status === 'pending_vendor_approval' ? { backgroundColor: ACCENT } : undefined}
                data-testid={`update-status-${order.order_id}`}
              >
                {order.status === 'pending_vendor_approval' && <ChefHat className="w-4 h-4 mr-1" />}
                {['confirmed', 'preparing'].includes(order.status) && <Package className="w-4 h-4 mr-1" />}
                {order.status === 'ready' && <CheckCircle className="w-4 h-4 mr-1" />}
                {config.nextLabel}
              </Button>
            )}
            {approveBlocked && order.status === 'pending_vendor_approval' && (
              <Button size="sm" disabled variant="outline" data-testid={`approve-blocked-${order.order_id}`}>
                Opens {opensOnLabel || 'later'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <PickupVerificationModal
        open={pickupModalOpen}
        order={order}
        onClose={() => setPickupModalOpen(false)}
        onVerified={handlePickupVerified}
      />
    </div>
  );
}
